/**
 * SISTEMA DE PERMISSÕES POR ROLE - VERSÃO ENTERPRISE
 * =================================================
 * 
 * Implementação completa do sistema de roles e permissões
 * conforme especificado pelo usuário
 */

export type UserRole =
  | 'backoffice'
  | 'comercial'
  | 'mesa'
  | 'gestor_backoffice'
  | 'gestor_comercial'
  | 'adm_master'
  | 'gestor_it'  // Gestor IT - gerencia equipe e aprova tickets de TI
  | 'it'  // IT Team - executa tickets de TI aprovados
  | 'logistica'  // Logística - valida logística, gerencia alocações e cargas
  | 'gestor_logistica'  // Gestor Logística - acesso total ao módulo logístico

export interface RolePermissions {
  // Acesso às telas principais
  canAccessInicio: boolean
  canAccessNegocios: boolean
  canAccessContratos: boolean
  canAccessBackoffice: boolean
  canAccessBackofficeTickets: boolean // Nova: acesso ao menu Tickets do backoffice
  canAccessConfiguracoes: boolean
  canAccessPrecos: boolean // Nova: acesso ao módulo de Preços
  canAccessMesaEstrategica: boolean // Acesso ao módulo Mesa Estratégica

  // Permissões de ação em Negócios
  canCreateNegocios: boolean
  canEditNegocios: boolean
  canViewNegocios: boolean

  // Permissões de ação em Backoffice
  canCreateBackoffice: boolean
  canEditBackoffice: boolean
  canViewBackoffice: boolean

  // Permissões de ação na Mesa (Validação)
  canValidateMesa?: boolean
  canRejectMesa?: boolean
  canViewMesaQueue?: boolean
  canViewPrecoMercado?: boolean

  // Permissões administrativas
  canManageUsers: boolean
  canAccessAdminPanel: boolean
  canViewReports: boolean
  canExportData: boolean

  // ===== InnovaDesk Permissions =====
  // Módulo de chamados internos (Sistema de tickets)
  canAccessInnovaDesk: boolean        // Acesso ao módulo InnovaDesk
  canCreateChamado: boolean            // Criar novos chamados
  canAssumirChamado: boolean           // Assumir chamados da fila (backoffice)
  canTransferirChamado: boolean        // Transferir chamados para outros atendentes
  canConcluirChamado: boolean          // Concluir chamados assumidos
  canAdminInnovaDeskTipos: boolean     // Administrar tipos de solicitação

  // ===== Logística Permissions =====
  canAccessLogistica: boolean              // Acesso ao módulo Logística
  canValidarLogistica: boolean             // Validar/rejeitar logística de solicitações
  canGerenciarAlocacoes: boolean           // Criar/editar/remover alocações
  canRegistrarCargas: boolean              // Registrar cargas
  canVerDashboardLogistica: boolean        // Ver KPIs e dashboard L/S
  canForcarConclusao: boolean              // Conclusão forçada (apenas gestor)
  canConfigurarDestinos: boolean           // CRUD locais/descrições (apenas gestor)

  // ===== IT Team Permissions (NEW) =====
  canAccessITTickets: boolean          // Acesso ao módulo de tickets de TI
  canApproveITTickets: boolean         // Aprovar/rejeitar tickets de TI (apenas IT-Master)
  canActOnITTickets: boolean           // Atuar em tickets de TI aprovados (IT-Developer, IT-AI)
  canManageITTeam: boolean             // Gerenciar membros da equipe de TI
}

/**
 * DEFINIÇÃO COMPLETA DE PERMISSÕES POR ROLE
 * =========================================
 */
export const ROLE_PERMISSIONS_MAP: Record<UserRole, RolePermissions> = {
  /**
   * BACKOFFICE
   * - Acessa: início, contratos e backoffice
   * - Não acessa: negócios, configurações
   * - Pode ver tickets e contratos, mas não cria negócios
   */
  backoffice: {
    canAccessInicio: true,
    canAccessNegocios: false,
    canAccessContratos: true,
    canAccessBackoffice: true,
    canAccessBackofficeTickets: true, // ✅ Vê Tickets no dropdown backoffice
    canAccessConfiguracoes: false,
    canAccessPrecos: false, // ❌ Backoffice NÃO acessa preços
    canAccessMesaEstrategica: false, // ❌ Backoffice NÃO acessa Mesa Estratégica

    canCreateNegocios: false,
    canEditNegocios: false,
    canViewNegocios: false,

    canCreateBackoffice: true,
    canEditBackoffice: true,
    canViewBackoffice: true,

    canValidateMesa: false,
    canRejectMesa: false,
    canViewMesaQueue: false,
    canViewPrecoMercado: false,

    canManageUsers: false,
    canAccessAdminPanel: false,
    canViewReports: false,
    canExportData: false,

    // InnovaDesk - Backoffice role
    canAccessInnovaDesk: true,          // ✅ Acessa InnovaDesk
    canCreateChamado: true,              // ✅ Pode criar chamados
    canAssumirChamado: true,             // ✅ Pode assumir chamados
    canTransferirChamado: false,         // ❌ NÃO pode transferir (apenas gestores)
    canConcluirChamado: true,            // ✅ Pode concluir chamados
    canAdminInnovaDeskTipos: false,      // ❌ Não administra tipos

    // Logística - Backoffice NÃO tem acesso logística
    canAccessLogistica: false,
    canValidarLogistica: false,
    canGerenciarAlocacoes: false,
    canRegistrarCargas: false,
    canVerDashboardLogistica: false,
    canForcarConclusao: false,
    canConfigurarDestinos: false,

    // IT Team - Backoffice NÃO tem acesso IT
    canAccessITTickets: false,
    canApproveITTickets: false,
    canActOnITTickets: false,
    canManageITTeam: false,
  },

  /**
   * COMERCIAL
   * - Acessa: início, negócios e contratos
   * - Não acessa: backoffice, configurações
   */
  comercial: {
    canAccessInicio: true,
    canAccessNegocios: true,
    canAccessContratos: true,
    canAccessBackoffice: false,
    canAccessBackofficeTickets: true, // ✅ Pode abrir /backoffice/tickets (mesmo sem acessar dropdown backoffice)
    canAccessConfiguracoes: false,
    canAccessPrecos: true, // ✅ Comercial PODE acessar preços (inserir)
    canAccessMesaEstrategica: false, // ❌ Comercial NÃO acessa Mesa Estratégica

    canCreateNegocios: true,
    canEditNegocios: true,
    canViewNegocios: true,

    canCreateBackoffice: false,
    canEditBackoffice: false,
    canViewBackoffice: false,

    canValidateMesa: false,
    canRejectMesa: false,
    canViewMesaQueue: false,
    canViewPrecoMercado: true,

    canManageUsers: false,
    canAccessAdminPanel: false,
    canViewReports: false,
    canExportData: false,

    // InnovaDesk - Comercial role
    canAccessInnovaDesk: true,          // ✅ Acessa InnovaDesk
    canCreateChamado: true,              // ✅ Pode criar chamados
    canAssumirChamado: false,            // ❌ Não assume (não é backoffice)
    canTransferirChamado: false,         // ❌ NÃO pode transferir
    canConcluirChamado: false,           // ❌ Não conclui
    canAdminInnovaDeskTipos: false,      // ❌ Não administra tipos

    // Logística - Comercial NÃO tem acesso logística
    canAccessLogistica: false,
    canValidarLogistica: false,
    canGerenciarAlocacoes: false,
    canRegistrarCargas: false,
    canVerDashboardLogistica: false,
    canForcarConclusao: false,
    canConfigurarDestinos: false,

    // IT Team - Comercial NÃO tem acesso IT
    canAccessITTickets: false,
    canApproveITTickets: false,
    canActOnITTickets: false,
    canManageITTeam: false,
  },

  /**
   * MESA (Validação)
   * - Acessa: início, contratos, preços, InnovaDesk
   * - Acessa backoffice MAS apenas para contratos (não tickets)
   * - Valida solicitações de contratos
   * - Pode criar chamados no InnovaDesk (mas não assumir/concluir)
   */
  mesa: {
    canAccessInicio: true,
    canAccessNegocios: false,
    canAccessContratos: true,
    canAccessBackoffice: true, // Acessa backoffice (mas apenas contratos)
    canAccessBackofficeTickets: true, // ✅ Vê Tickets no dropdown backoffice para resolver chamados relacionados à Mesa
    canAccessConfiguracoes: false,
    canAccessPrecos: true, // ✅ Mesa PODE acessar preços (consultar apenas)
    canAccessMesaEstrategica: true, // ✅ Mesa acessa Mesa Estratégica

    canCreateNegocios: false,
    canEditNegocios: false,
    canViewNegocios: false,

    canCreateBackoffice: false,
    canEditBackoffice: false,
    canViewBackoffice: true, // Apenas visualização

    canValidateMesa: true, // ⭐ PERMISSÃO EXCLUSIVA
    canRejectMesa: true,   // ⭐ PERMISSÃO EXCLUSIVA
    canViewMesaQueue: true, // ⭐ PERMISSÃO EXCLUSIVA
    canViewPrecoMercado: true,

    canManageUsers: false,
    canAccessAdminPanel: false,
    canViewReports: false,
    canExportData: false,

    // InnovaDesk - Mesa role (ACESSO PARA CRIAR CHAMADOS)
    canAccessInnovaDesk: true,           // ✅ Mesa PODE acessar InnovaDesk
    canCreateChamado: true,              // ✅ Mesa PODE criar chamados
    canAssumirChamado: false,            // ❌ Não assume (não é backoffice)
    canTransferirChamado: false,         // ❌ NÃO pode transferir
    canConcluirChamado: false,           // ❌ Não conclui
    canAdminInnovaDeskTipos: false,      // ❌ Não administra tipos

    // Logística - Mesa: acesso completo às operações logísticas
    canAccessLogistica: true,            // ✅ Acessa módulo
    canValidarLogistica: true,           // ✅ Aprovar/remover aprovação de planejamentos
    canGerenciarAlocacoes: true,         // ✅ Criar/editar planejamentos L/S
    canRegistrarCargas: true,            // ✅ Registrar cargas
    canVerDashboardLogistica: true,      // ✅ Vê dashboard L/S
    canForcarConclusao: true,            // ✅ Forçar conclusão
    canConfigurarDestinos: true,         // ✅ Configurar destinos

    // IT Team - Mesa NÃO tem acesso IT
    canAccessITTickets: false,
    canApproveITTickets: false,
    canActOnITTickets: false,
    canManageITTeam: false,
  },

  /**
   * GESTOR BACKOFFICE
   * - Acesso liberado a tudo
   * - Só consegue VISUALIZAR negócios (não criar/editar)
   */
  gestor_backoffice: {
    canAccessInicio: true,
    canAccessNegocios: false, // Solicitar contrato é do COMERCIAL (CEO, 10/08). O backoffice OPERA a solicitação em /backoffice-contratos, não a abre. Tickets seguem por canAccessInnovaDesk,
    canAccessContratos: true,
    canAccessBackoffice: true,
    canAccessBackofficeTickets: true, // ✅ Vê tudo no backoffice
    canAccessConfiguracoes: true,
    canAccessPrecos: true, // ✅ Gestor PODE acessar preços (acesso total)
    canAccessMesaEstrategica: true, // ✅ Gestor Backoffice acessa Mesa Estratégica

    canCreateNegocios: false, // ⭐ APENAS VISUALIZAÇÃO
    canEditNegocios: false,   // ⭐ APENAS VISUALIZAÇÃO
    canViewNegocios: true,

    canCreateBackoffice: true,
    canEditBackoffice: true,
    canViewBackoffice: true,

    canValidateMesa: true,
    canRejectMesa: true,
    canViewMesaQueue: true,
    canViewPrecoMercado: true,

    // ❌ Gestão de usuário é só adm_master. Gravar `role` e `is_aprovador` é
    // reescrever o mapa de permissões de outra pessoa — inclusive a tag, que
    // concede aprovar pagamento e editar rolagem. Espelha `ADMIN_ROLES` de
    // `app/api/usuarios/route.ts`; o gestor segue vendo o resto de Configurações.
    canManageUsers: false,
    canAccessAdminPanel: true,
    canViewReports: true,
    canExportData: true,

    // InnovaDesk - Gestor Backoffice (FULL ACCESS)
    canAccessInnovaDesk: true,          // ✅ Acessa InnovaDesk
    canCreateChamado: true,              // ✅ Pode criar chamados
    canAssumirChamado: true,             // ✅ Pode assumir chamados
    canTransferirChamado: true,          // ✅ PODE transferir chamados
    canConcluirChamado: true,            // ✅ Pode concluir chamados
    canAdminInnovaDeskTipos: true,       // ✅ Administra tipos

    // Logística - Gestor Backoffice NÃO tem acesso logística
    canAccessLogistica: false,
    canValidarLogistica: false,
    canGerenciarAlocacoes: false,
    canRegistrarCargas: false,
    canVerDashboardLogistica: false,
    canForcarConclusao: false,
    canConfigurarDestinos: false,

    // IT Team - Gestor Backoffice pode VER mas não gerenciar IT
    canAccessITTickets: true,            // ✅ Pode ver tickets IT
    canApproveITTickets: false,          // ❌ Não aprova (apenas IT-Master)
    canActOnITTickets: false,            // ❌ Não atua em tickets IT
    canManageITTeam: false,              // ❌ Não gerencia equipe IT
  },

  /**
   * GESTOR COMERCIAL
   * - PODE visualizar backoffice (contratos e tickets) mas NÃO PODE OPERAR
   * - NÃO pode aprovar, rejeitar, editar, devolver ou criar contratos no backoffice
   * - NÃO pode validar/rejeitar na Mesa (apenas visualização)
   * - Pode criar/editar negócios (diferente do gestor_backoffice)
   * - Gerencia usuários, relatórios, configurações
   * - Acesso total ao InnovaDesk (assume, transfere, administra)
   */
  gestor_comercial: {
    canAccessInicio: true,
    canAccessNegocios: true,
    canAccessContratos: true,
    canAccessBackoffice: true,        // ✅ PODE acessar backoffice (somente visualização)
    canAccessBackofficeTickets: true, // ✅ PODE ver tickets do backoffice (somente visualização)
    canAccessConfiguracoes: true,
    canAccessPrecos: true, // ✅ Gestor PODE acessar preços (acesso total)
    canAccessMesaEstrategica: true, // ✅ Gestor Comercial acessa Mesa Estratégica

    canCreateNegocios: true,  // ✅ PODE criar (diferente do gestor_backoffice)
    canEditNegocios: true,    // ✅ PODE editar (diferente do gestor_backoffice)
    canViewNegocios: true,

    canCreateBackoffice: false, // ❌ NÃO pode criar no backoffice
    canEditBackoffice: false,   // ❌ NÃO pode editar no backoffice
    canViewBackoffice: true,    // ✅ PODE visualizar backoffice

    canValidateMesa: false,     // ❌ NÃO pode validar (apenas visualização)
    canRejectMesa: false,       // ❌ NÃO pode rejeitar (apenas visualização)
    canViewMesaQueue: true,     // ✅ PODE ver fila (apenas visualização)
    canViewPrecoMercado: true,

    canManageUsers: false,      // ❌ Gestão de usuário é só adm_master (ver nota em gestor_backoffice)
    canAccessAdminPanel: true,  // ✅ Acessa admin
    canViewReports: true,       // ✅ Relatórios
    canExportData: true,        // ✅ Exporta dados

    // InnovaDesk - Gestor Comercial (FULL ACCESS - mesma autonomia gestor_backoffice)
    canAccessInnovaDesk: true,          // ✅ Acessa InnovaDesk
    canCreateChamado: true,              // ✅ Pode criar chamados
    canAssumirChamado: true,             // ✅ PODE assumir (autonomia completa)
    canTransferirChamado: true,          // ✅ PODE transferir (autonomia completa)
    canConcluirChamado: true,            // ✅ PODE concluir (autonomia completa)
    canAdminInnovaDeskTipos: true,       // ✅ Administra tipos (autonomia completa)

    // Logística - Gestor Comercial: acesso completo às operações logísticas
    canAccessLogistica: true,            // ✅ Acessa módulo
    canValidarLogistica: true,           // ✅ Aprovar/remover aprovação de planejamentos
    canGerenciarAlocacoes: true,         // ✅ Criar/editar planejamentos L/S
    canRegistrarCargas: true,            // ✅ Registrar cargas
    canVerDashboardLogistica: true,      // ✅ Vê dashboard L/S
    canForcarConclusao: true,            // ✅ Forçar conclusão
    canConfigurarDestinos: true,         // ✅ Configurar destinos

    // IT Team - Gestor Comercial pode VER mas não gerenciar IT
    canAccessITTickets: true,            // ✅ Pode ver tickets IT
    canApproveITTickets: false,          // ❌ Não aprova (apenas IT-Master)
    canActOnITTickets: false,            // ❌ Não atua em tickets IT
    canManageITTeam: false,              // ❌ Não gerencia equipe IT
  },

  /**
   * ADM MASTER
   * - Acesso 100% a tudo
   * - Permissões completas
   */
  adm_master: {
    canAccessInicio: true,
    canAccessNegocios: true,
    canAccessContratos: true,
    canAccessBackoffice: true,
    canAccessBackofficeTickets: true, // ✅ Acesso total
    canAccessConfiguracoes: true,
    canAccessPrecos: true, // ✅ ADM Master PODE acessar tudo (acesso total)
    canAccessMesaEstrategica: true, // ✅ ADM Master acessa Mesa Estratégica

    canCreateNegocios: true,
    canEditNegocios: true,
    canViewNegocios: true,

    canCreateBackoffice: true,
    canEditBackoffice: true,
    canViewBackoffice: true,

    canValidateMesa: true,
    canRejectMesa: true,
    canViewMesaQueue: true,
    canViewPrecoMercado: true,

    canManageUsers: true,
    canAccessAdminPanel: true,
    canViewReports: true,
    canExportData: true,

    // InnovaDesk - ADM Master (FULL ACCESS)
    canAccessInnovaDesk: true,          // ✅ Acessa InnovaDesk
    canCreateChamado: true,              // ✅ Pode criar chamados
    canAssumirChamado: true,             // ✅ Pode assumir chamados
    canTransferirChamado: true,          // ✅ PODE transferir chamados
    canConcluirChamado: true,            // ✅ Pode concluir chamados
    canAdminInnovaDeskTipos: true,       // ✅ Administra tipos

    // Logística - ADM Master tem ACESSO TOTAL
    canAccessLogistica: true,
    canValidarLogistica: true,
    canGerenciarAlocacoes: true,
    canRegistrarCargas: true,
    canVerDashboardLogistica: true,
    canForcarConclusao: true,
    canConfigurarDestinos: true,

    // IT Team - ADM Master tem ACESSO TOTAL a tudo
    canAccessITTickets: true,            // ✅ Acesso total
    canApproveITTickets: true,           // ✅ Pode aprovar
    canActOnITTickets: true,             // ✅ Pode atuar
    canManageITTeam: true,               // ✅ Gerencia equipe IT
  },

  /**
   * GESTOR IT (Gestor de Tecnologia)
   * - Acessa APENAS Tickets e Solicitações (assim como role IT)
   * - Pode gerenciar tipos de solicitação do InnovaDesk
   * - Gerencia equipe de TI
   * - Rota padrão: /backoffice/tickets
   */
  gestor_it: {
    canAccessInicio: false,             // ❌ NÃO acessa início (vai direto para Tickets)
    canAccessNegocios: false,
    canAccessContratos: false,          // ❌ NÃO acessa contratos
    canAccessBackoffice: false,         // ❌ NÃO acessa dropdown Backoffice
    canAccessBackofficeTickets: true,   // ✅ PODE acessar a rota /backoffice/tickets
    canAccessConfiguracoes: false,      // ❌ NÃO acessa configurações gerais
    canAccessPrecos: false,
    canAccessMesaEstrategica: false, // ❌ Gestor IT NÃO acessa Mesa Estratégica

    canCreateNegocios: false,
    canEditNegocios: false,
    canViewNegocios: false,

    canCreateBackoffice: false,
    canEditBackoffice: false,
    canViewBackoffice: false,

    canValidateMesa: false,
    canRejectMesa: false,
    canViewMesaQueue: false,
    canViewPrecoMercado: false,

    canManageUsers: false,              // ❌ NÃO gerencia usuários
    canAccessAdminPanel: false,
    canViewReports: false,
    canExportData: false,

    // InnovaDesk - Gestor IT pode criar e gerenciar tipos
    canAccessInnovaDesk: true,          // ✅ Acessa Solicitações
    canCreateChamado: true,
    canAssumirChamado: true,            // ✅ Pode assumir chamados
    canTransferirChamado: true,         // ✅ Pode transferir chamados
    canConcluirChamado: true,           // ✅ Pode concluir chamados
    canAdminInnovaDeskTipos: true,      // ✅ Pode administrar tipos de solicitação

    // Logística - Gestor IT NÃO tem acesso logística
    canAccessLogistica: false,
    canValidarLogistica: false,
    canGerenciarAlocacoes: false,
    canRegistrarCargas: false,
    canVerDashboardLogistica: false,
    canForcarConclusao: false,
    canConfigurarDestinos: false,

    // IT Team - GESTOR IT tem controle sobre tickets de TI
    canAccessITTickets: false,          // ❌ Não usa módulo IT-tickets separado
    canApproveITTickets: false,
    canActOnITTickets: false,
    canManageITTeam: false,
  },

  /**
   * IT (Tecnologia e Inovação)
   * - Acessa Tickets (se equipe tem is_suporte = true) e Solicitações
   * - Pode criar chamados gerais no InnovaDesk
   * - Equipe Tecnologia é de suporte, então vê Tickets
   */
  it: {
    canAccessInicio: false,             // ❌ NÃO acessa início (vai direto para Tickets)
    canAccessNegocios: false,
    canAccessContratos: false,
    canAccessBackoffice: false,         // ❌ Não acessa dropdown Backoffice
    canAccessBackofficeTickets: true,   // ✅ PODE acessar a rota /backoffice/tickets
    canAccessConfiguracoes: false,
    canAccessPrecos: false,
    canAccessMesaEstrategica: false, // ❌ IT NÃO acessa Mesa Estratégica

    canCreateNegocios: false,
    canEditNegocios: false,
    canViewNegocios: false,

    canCreateBackoffice: false,
    canEditBackoffice: false,
    canViewBackoffice: false,

    canValidateMesa: false,
    canRejectMesa: false,
    canViewMesaQueue: false,
    canViewPrecoMercado: false,

    canManageUsers: false,
    canAccessAdminPanel: false,
    canViewReports: false,
    canExportData: false,

    // InnovaDesk - IT pode criar chamados gerais
    canAccessInnovaDesk: true,
    canCreateChamado: true,
    canAssumirChamado: true,            // ✅ Pode assumir chamados (equipe de suporte)
    canTransferirChamado: false,
    canConcluirChamado: true,           // ✅ Pode concluir chamados assumidos
    canAdminInnovaDeskTipos: false,

    // Logística - IT NÃO tem acesso logística
    canAccessLogistica: false,
    canValidarLogistica: false,
    canGerenciarAlocacoes: false,
    canRegistrarCargas: false,
    canVerDashboardLogistica: false,
    canForcarConclusao: false,
    canConfigurarDestinos: false,

    // IT Team - Access granted, capabilities determined by team sub-role
    canAccessITTickets: false,          // ❌ Não usa mais o módulo IT-tickets separado
    canApproveITTickets: false,         // ❌ Base permission - team sub-role (master) overrides
    canActOnITTickets: false,           // ❌ Base permission - team sub-role overrides
    canManageITTeam: false,             // ❌ Base permission - team sub-role (master) overrides
  },

  /**
   * LOGÍSTICA
   * - Acessa: início, logística
   * - Valida logística, gerencia alocações e registra cargas
   * - NÃO pode forçar conclusão nem configurar destinos
   */
  logistica: {
    canAccessInicio: true,
    canAccessNegocios: false,
    canAccessContratos: true,           // ✅ IA-745: Dashboard, Ficha, Posição, PNL
    canAccessBackoffice: false,
    canAccessBackofficeTickets: false,
    canAccessConfiguracoes: false,
    canAccessPrecos: false,
    canAccessMesaEstrategica: false, // ❌ Logística NÃO acessa Mesa Estratégica

    canCreateNegocios: false,
    canEditNegocios: false,
    canViewNegocios: false,

    canCreateBackoffice: false,
    canEditBackoffice: false,
    canViewBackoffice: false,

    canValidateMesa: false,
    canRejectMesa: false,
    canViewMesaQueue: false,
    canViewPrecoMercado: false,

    canManageUsers: false,
    canAccessAdminPanel: false,
    canViewReports: false,
    canExportData: false,

    // InnovaDesk - Logística pode criar chamados
    canAccessInnovaDesk: true,
    canCreateChamado: true,
    canAssumirChamado: false,
    canTransferirChamado: false,
    canConcluirChamado: false,
    canAdminInnovaDeskTipos: false,

    // Logística - Acesso operacional
    canAccessLogistica: true,              // ✅ Acessa módulo logística
    canValidarLogistica: true,             // ✅ Valida/rejeita logística
    canGerenciarAlocacoes: true,           // ✅ Gerencia alocações
    canRegistrarCargas: true,              // ✅ Registra cargas
    canVerDashboardLogistica: true,        // ✅ Vê dashboard L/S
    canForcarConclusao: true,              // ✅ Pode forçar conclusão
    canConfigurarDestinos: true,           // ✅ Pode configurar destinos

    // IT Team - Logística NÃO tem acesso IT
    canAccessITTickets: false,
    canApproveITTickets: false,
    canActOnITTickets: false,
    canManageITTeam: false,
  },

  /**
   * GESTOR LOGÍSTICA
   * - Acessa: início, logística, configurações de destinos
   * - Todas as permissões de logística + forçar conclusão + configurar destinos
   * - Acesso total ao módulo logístico
   */
  gestor_logistica: {
    canAccessInicio: true,
    canAccessNegocios: false,
    canAccessContratos: false,
    canAccessBackoffice: false,
    canAccessBackofficeTickets: false,
    canAccessConfiguracoes: false,
    canAccessPrecos: false,
    canAccessMesaEstrategica: false, // ❌ Gestor Logística NÃO acessa Mesa Estratégica

    canCreateNegocios: false,
    canEditNegocios: false,
    canViewNegocios: false,

    canCreateBackoffice: false,
    canEditBackoffice: false,
    canViewBackoffice: false,

    canValidateMesa: false,
    canRejectMesa: false,
    canViewMesaQueue: false,
    canViewPrecoMercado: false,

    canManageUsers: false,
    canAccessAdminPanel: false,
    canViewReports: true,              // ✅ Gestor pode ver relatórios
    canExportData: true,               // ✅ Gestor pode exportar dados

    // InnovaDesk - Gestor Logística
    canAccessInnovaDesk: true,
    canCreateChamado: true,
    canAssumirChamado: false,
    canTransferirChamado: false,
    canConcluirChamado: false,
    canAdminInnovaDeskTipos: false,

    // Logística - ACESSO TOTAL ao módulo
    canAccessLogistica: true,              // ✅ Acessa módulo logística
    canValidarLogistica: true,             // ✅ Valida/rejeita logística
    canGerenciarAlocacoes: true,           // ✅ Gerencia alocações
    canRegistrarCargas: true,              // ✅ Registra cargas
    canVerDashboardLogistica: true,        // ✅ Vê dashboard L/S
    canForcarConclusao: true,              // ✅ PODE forçar conclusão
    canConfigurarDestinos: true,           // ✅ PODE configurar destinos

    // IT Team - Gestor Logística NÃO tem acesso IT
    canAccessITTickets: false,
    canApproveITTickets: false,
    canActOnITTickets: false,
    canManageITTeam: false,
  },
}

/**
 * UTILITÁRIOS DE PERMISSÕES
 * =========================
 */
export class RolePermissionManager {
  /**
   * Obter permissões para um role específico
   */
  static getPermissions(role: UserRole): RolePermissions {
    return ROLE_PERMISSIONS_MAP[role] || ROLE_PERMISSIONS_MAP.comercial
  }

  /**
   * Verificar se usuário tem permissão específica
   */
  static hasPermission(role: UserRole, permission: keyof RolePermissions): boolean {
    const permissions = this.getPermissions(role)
    return permissions[permission] === true
  }

  /**
   * Verificar acesso a rota específica
   */
  static canAccessRoute(role: UserRole, route: string): boolean {
    // Rotas públicas que não precisam de verificação
    const publicRoutes = [
      '/login',
      '/cadastro', 
      '/api',
      '/favicon.ico',
      '/manifest.json',
      '/_next',
      '/images',
      '/icons'
    ]
    
    // Se é rota pública, permitir acesso
    if (publicRoutes.some(publicRoute => route.startsWith(publicRoute))) {
      return true
    }
    
    const permissions = this.getPermissions(role)

    // Verificar rotas usando startsWith para cobrir sub-rotas
    if (route.startsWith('/inicio')) return permissions.canAccessInicio
    if (route.startsWith('/negocios')) return permissions.canAccessNegocios
    if (route.startsWith('/contratos')) return permissions.canAccessContratos
    if (route.startsWith('/contrato')) return permissions.canAccessContratos
    if (route.startsWith('/backoffice/tickets')) return permissions.canAccessBackofficeTickets
    if (route.startsWith('/backoffice-contratos')) return permissions.canAccessBackoffice
    if (route.startsWith('/gestao-pagamentos')) {
      const financeiro: UserRole[] = ['backoffice', 'gestor_backoffice', 'gestor_comercial', 'adm_master']
      return financeiro.includes(role)
    }
    if (route.startsWith('/configuracoes')) return permissions.canAccessConfiguracoes
    if (route.startsWith('/admin-users')) return permissions.canAccessConfiguracoes
    if (route.startsWith('/parametros')) return permissions.canAccessConfiguracoes
    if (route.startsWith('/pnl')) return permissions.canAccessBackoffice || permissions.canAccessLogistica || permissions.canAccessContratos
    if (route.startsWith('/precos')) return permissions.canAccessPrecos
    if (route.startsWith('/innova-desk')) return permissions.canAccessInnovaDesk
    if (route.startsWith('/logistica')) return permissions.canAccessLogistica
    if (route.startsWith('/it-tickets')) return permissions.canAccessITTickets

    // Para rotas não mapeadas, permitir acesso se o usuário tem pelo menos uma permissão básica
    return permissions.canAccessInicio
  }

  /**
   * Obter lista de rotas permitidas para um role
   */
  static getAllowedRoutes(role: UserRole): string[] {
    const permissions = this.getPermissions(role)
    const routes: string[] = []

    // Para roles de logística, a primeira rota deve ser /logistica
    const isLogisticaRole = role === 'logistica' || role === 'gestor_logistica'
    if (isLogisticaRole && permissions.canAccessLogistica) {
      routes.push('/logistica')
    }

    // Para roles de TI (it e gestor_it), a primeira rota deve ser /backoffice/tickets
    const isITRole = role === 'it' || role === 'gestor_it'
    if (isITRole && permissions.canAccessBackofficeTickets) {
      routes.push('/backoffice/tickets')
    }

    if (permissions.canAccessInicio) routes.push('/inicio')
    if (permissions.canAccessNegocios) routes.push('/negocios')
    if (permissions.canAccessContratos) routes.push('/contratos')
    if (permissions.canAccessBackoffice) routes.push('/backoffice-contratos')
    if (permissions.canAccessBackofficeTickets && !isITRole) routes.push('/backoffice/tickets')
    if (permissions.canAccessPrecos) routes.push('/precos')
    if (permissions.canAccessInnovaDesk) routes.push('/innova-desk')
    if (permissions.canAccessLogistica && !isLogisticaRole) routes.push('/logistica')
    if (permissions.canAccessITTickets) routes.push('/it-tickets')
    if (permissions.canAccessConfiguracoes) {
      routes.push('/configuracoes')
      routes.push('/admin-users')
      routes.push('/parametros')
    }

    return routes
  }

  /**
   * Validar se role é válido
   */
  static isValidRole(role: string): role is UserRole {
    return Object.keys(ROLE_PERMISSIONS_MAP).includes(role)
  }

  /**
   * Obter descrição amigável do role
   */
  static getRoleDisplayName(role: UserRole): string {
    const displayNames: Record<UserRole, string> = {
      backoffice: 'Backoffice - Admin. interna',
      comercial: 'Comercial - Vendas',
      mesa: 'Mesa - Validação e Conformidade',
      gestor_backoffice: 'Gestor Backoffice - Gestão avançada',
      gestor_comercial: 'Gestor Comercial - Gestão avançada',
      adm_master: 'ADM Master - Acesso total',
      // IT Team Roles
      gestor_it: 'Gestor IT - Gestão de TI',
      it: 'IT - Tecnologia',
      logistica: 'Logística - Operações logísticas',
      gestor_logistica: 'Gestor Logística - Gestão logística'
    }

    return displayNames[role] || role
  }

  /**
   * Obter todos os roles disponíveis
   */
  static getAllRoles(): UserRole[] {
    return Object.keys(ROLE_PERMISSIONS_MAP) as UserRole[]
  }

  /**
   * Obter roles para seleção em formulários
   */
  static getRoleOptions(): Array<{ value: UserRole; label: string }> {
    return this.getAllRoles().map(role => ({
      value: role,
      label: this.getRoleDisplayName(role)
    }))
  }
}

/**
 * CONSTANTES ÚTEIS
 * ================
 */
export const DEFAULT_ROLE: UserRole = 'comercial'